let encoderInstance;

const ESC = 0x1B;
const GS = 0x1D;
const NEW_LINE = 0x0A;

const ensureEncoder = () => {
  if (encoderInstance) return encoderInstance;

  if (typeof TextEncoder !== 'undefined') {
    encoderInstance = new TextEncoder();
    return encoderInstance;
  }

  try {
    // eslint-disable-next-line global-require
    const { TextEncoder: PolyfillTextEncoder } = require('util');
    encoderInstance = new PolyfillTextEncoder();
    return encoderInstance;
  } catch (error) {
    throw new Error('TextEncoder is not available in this environment');
  }
};

const textToBytes = (text = '') => {
  const encoder = ensureEncoder();
  return Array.from(encoder.encode(String(text)));
};

const padText = (text, width, align = 'left') => {
  const str = String(text ?? '');
  if (str.length >= width) return str.slice(0, width);
  const padding = ' '.repeat(width - str.length);
  if (align === 'right') return padding + str;
  if (align === 'center') {
    const half = Math.floor(padding.length / 2);
    return padding.slice(0, half) + str + padding.slice(half);
  }
  return str + padding;
};

const getCurrencySymbol = (currency = 'INR') => {
  const upper = String(currency || '').toUpperCase();
  switch (upper) {
    case 'INR':
      // Use 'Rs' instead of ₹ for better ESC/POS compatibility
      return 'Rs';
    case 'USD':
      return '$';
    case 'EUR':
      return 'EUR';
    case 'GBP':
      return 'GBP';
    case 'JPY':
      return 'JPY';
    default:
      return '';
  }
};

const formatAmount = (amount = 0, currency = 'INR') => {
  const numeric = Number.isFinite(amount) ? amount : Number(amount) || 0;
  const symbol = getCurrencySymbol(currency);
  const value = numeric.toFixed(2);
  if (!symbol) {
    return `${currency} ${value}`;
  }
  return `${symbol}${value}`;
};

const pushBytes = (buffer, ...bytes) => {
  buffer.push(...bytes);
};

const appendLine = (buffer, text = '') => {
  if (text) {
    pushBytes(buffer, ...textToBytes(text));
  }
  pushBytes(buffer, NEW_LINE);
};

const setAlignment = (buffer, alignment = 'left') => {
  const map = { left: 0x00, center: 0x01, right: 0x02 };
  const value = map[alignment] ?? 0x00;
  pushBytes(buffer, ESC, 0x61, value);
};

const setBold = (buffer, enabled) => {
  pushBytes(buffer, ESC, 0x45, enabled ? 0x01 : 0x00);
};

const setDoubleHeightWidth = (buffer, enabled) => {
  pushBytes(buffer, ESC, 0x21, enabled ? 0x30 : 0x00);
};

const feedLines = (buffer, count = 1) => {
  const lines = Math.max(1, Math.min(255, count));
  pushBytes(buffer, ESC, 0x64, lines);
};

const cutPaper = (buffer, mode = 'partial') => {
  const command = mode === 'full' ? 0x00 : 0x01;
  pushBytes(buffer, GS, 0x56, command);
};

const appendSeparator = (buffer, width = 32) => {
  appendLine(buffer, '-'.repeat(width));
};

// Convert Firestore timestamp to Date
const toDate = (value) => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch {
      return new Date();
    }
  }
  if (value && typeof value === 'object' && '_seconds' in value) {
    return new Date(value._seconds * 1000);
  }
  if (value && typeof value === 'object' && 'seconds' in value) {
    return new Date(value.seconds * 1000);
  }
  return new Date(value);
};

const appendItemLines = (buffer, item, currency, width = 32) => {
  const safeName = (item.name || 'Item').toString();
  appendLine(buffer, padText(safeName, width));

  const qtyText = `${Number(item.quantity || 0)} x ${formatAmount(item.unitPrice || 0, currency)}`;
  const totalAmount = item.lineTotal != null
    ? item.lineTotal
    : Number(item.quantity || 0) * Number(item.unitPrice || 0);
  
  // Show item-level discount if applicable
  const hasItemDiscount = item.discount && item.discount > 0;
  const originalTotal = item.originalTotal || (Number(item.quantity || 0) * Number(item.unitPrice || 0));
  
  if (hasItemDiscount) {
    // Show quantity and price
    appendLine(buffer, padText(qtyText, width));
    
    // Show original price
    const originalText = formatAmount(originalTotal, currency);
    appendLine(buffer, padText(`Original: ${originalText}`, width));
    
    // Show discount amount with spacing
    setBold(buffer, true);
    const discountText = `-${formatAmount(item.discount, currency)}`;
    appendLine(buffer, padText(`Discount: ${discountText}`, width));
    setBold(buffer, false);
    
    // Show final price
    const totalText = formatAmount(totalAmount, currency);
    const detailWidth = Math.max(0, width - totalText.length);
    appendLine(buffer, `${padText('Total:', detailWidth)}${totalText}`);
  } else {
    const totalText = formatAmount(totalAmount, currency);
    const detailWidth = Math.max(0, width - totalText.length);
    appendLine(buffer, `${padText(qtyText, detailWidth)}${totalText}`);
  }

  if (item.notes) {
    appendLine(buffer, padText(item.notes, width));
  }
};

export const encodePrintableInvoice = (printableInvoice, options = {}) => {
  if (!printableInvoice) {
    throw new Error('Printable invoice is required');
  }

  const lineWidth = options.lineWidth || 32;
  const currency = printableInvoice.currency || 'INR';
  const buffer = [];

  // Initialize printer
  pushBytes(buffer, ESC, 0x40);

  // Business header
  setAlignment(buffer, 'center');
  setBold(buffer, true);
  setDoubleHeightWidth(buffer, true);
  appendLine(buffer, printableInvoice.business?.name || 'Business');
  setDoubleHeightWidth(buffer, false);
  setBold(buffer, false);

  if (printableInvoice.business?.address) {
    appendLine(buffer, printableInvoice.business.address);
  }
  if (printableInvoice.business?.phone) {
    appendLine(buffer, `Phone: ${printableInvoice.business.phone}`);
  }
  if (printableInvoice.business?.taxId) {
    appendLine(buffer, `GST: ${printableInvoice.business.taxId}`);
  }

  feedLines(buffer, 1);
  setAlignment(buffer, 'left');
  appendSeparator(buffer, lineWidth);

  // Invoice metadata
  appendLine(buffer, `Invoice: ${printableInvoice.invoiceNumber || '-'}`);
  const invoiceDate = toDate(printableInvoice.issueDate || printableInvoice.metadata?.createdAt || Date.now());
  // Format date and time in local timezone
  const dateStr = invoiceDate.toLocaleDateString('en-IN', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  });
  const timeStr = invoiceDate.toLocaleTimeString('en-IN', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
  appendLine(buffer, `Date: ${dateStr} ${timeStr}`);
  // Account ID removed - should not be printed on receipt

  feedLines(buffer, 1);
  appendLine(buffer, `Customer: ${printableInvoice.customer?.name || 'Walk-in'}`);
  if (printableInvoice.customer?.phone) {
    appendLine(buffer, `Phone: ${printableInvoice.customer.phone}`);
  }
  if (printableInvoice.customer?.address) {
    appendLine(buffer, `Address: ${printableInvoice.customer.address}`);
  }

  feedLines(buffer, 1);
  appendSeparator(buffer, lineWidth);
  appendLine(buffer, padText('Items', lineWidth));
  appendSeparator(buffer, lineWidth);

  const items = printableInvoice.items || [];
  items.forEach((item) => appendItemLines(buffer, item, currency, lineWidth));

  appendSeparator(buffer, lineWidth);
  setAlignment(buffer, 'right');

  const totals = printableInvoice.totals || {};
  const hasDiscount = totals.discount && totals.discount > 0;
  
  // Calculate item-level discount total
  const itemDiscountTotal = (printableInvoice.items || []).reduce((sum, item) => sum + (item.discount || 0), 0);
  const hasItemDiscounts = itemDiscountTotal > 0;
  const overallDiscount = totals.discount - itemDiscountTotal;
  
  appendLine(buffer, `Subtotal: ${formatAmount(totals.subTotal ?? totals.lineTotal ?? 0, currency)}`);
  
  // Show item-level discounts summary if any
  if (hasItemDiscounts) {
    setBold(buffer, true);
    appendLine(buffer, `Item Discounts: -${formatAmount(itemDiscountTotal, currency)}`);
    setBold(buffer, false);
  }
  
  // Show overall discount if any
  if (hasDiscount && overallDiscount > 0) {
    setBold(buffer, true);
    appendLine(buffer, `Overall Discount: -${formatAmount(overallDiscount, currency)}`);
    setBold(buffer, false);
  }
  
  // Show total discount and savings (always show if discount exists)
  if (hasDiscount || hasItemDiscounts) {
    feedLines(buffer, 1);
    setAlignment(buffer, 'center');
    setBold(buffer, true);
    setDoubleHeightWidth(buffer, true);
    const totalSavings = totals.discount || itemDiscountTotal;
    appendLine(buffer, `YOU SAVED ${formatAmount(totalSavings, currency)}`);
    setDoubleHeightWidth(buffer, false);
    setBold(buffer, false);
    setAlignment(buffer, 'right');
    feedLines(buffer, 1);
  }

  if (totals.tax) {
    appendLine(buffer, `Tax: ${formatAmount(totals.tax, currency)}`);
  }

  if (totals.serviceCharge) {
    appendLine(buffer, `Service: ${formatAmount(totals.serviceCharge, currency)}`);
  }

  setBold(buffer, true);
  appendLine(buffer, `Total: ${formatAmount(totals.grandTotal ?? 0, currency)}`);
  setBold(buffer, false);

  if (totals.paid) {
    appendLine(buffer, `Paid: ${formatAmount(totals.paid, currency)}`);
  }

  if (totals.balanceDue) {
    appendLine(buffer, `Due: ${formatAmount(totals.balanceDue, currency)}`);
  }

  setAlignment(buffer, 'left');
  feedLines(buffer, 1);

  if (printableInvoice.payment?.method) {
    appendLine(buffer, `Payment Method: ${printableInvoice.payment.method}`);
  }
  if (printableInvoice.metadata?.notes) {
    appendLine(buffer, `Notes: ${printableInvoice.metadata.notes}`);
  }

  feedLines(buffer, 1);
  setAlignment(buffer, 'center');
  // Use custom footer text from metadata or options, fallback to default
  const footerText = printableInvoice.metadata?.footerText || options.footerText || 'Thank you for your business!';
  appendLine(buffer, footerText);
  if (options.footerText && options.footerText !== footerText) {
    appendLine(buffer, options.footerText);
  }

  feedLines(buffer, options.feedLines ?? 4);

  if (options.autoCut) {
    cutPaper(buffer, options.cutMode || 'partial');
  }

  return Uint8Array.from(buffer);
};

export const encodeTestPrint = (options = {}) => {
  const buffer = [];
  const businessName = options.businessName || 'TrackProfit';

  pushBytes(buffer, ESC, 0x40);
  setAlignment(buffer, 'center');
  setBold(buffer, true);
  appendLine(buffer, `${businessName}`);
  setBold(buffer, false);
  appendLine(buffer, 'Thermal Printer Test');
  appendSeparator(buffer, 32);
  setAlignment(buffer, 'left');
  appendLine(buffer, `Date: ${new Date().toLocaleString()}`);
  appendLine(buffer, 'Status: ✅ Printer reachable');
  appendSeparator(buffer, 32);
  setAlignment(buffer, 'center');
  appendLine(buffer, '--- END OF TEST ---');
  feedLines(buffer, options.feedLines ?? 4);
  if (options.autoCut) {
    cutPaper(buffer, options.cutMode || 'partial');
  }
  return Uint8Array.from(buffer);
};



